// src/propertyManagement/pages/VendorDetailScreen.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  Paper,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DataGrid } from '@mui/x-data-grid';
import { db } from '../../firebase';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  getDoc,
  doc
} from 'firebase/firestore';
import EFTPaymentModal from './EFTPaymentModal';
import { dummyVendors } from './VendorsPage';

const tabLabels = ['Summary', 'Financials', 'Communications', 'Files', 'Notes'];

export default function VendorDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const vendor = dummyVendors.find(v => v.id === +id);
  const vendorId = id;
 const [openInstant, setOpenInstant] = useState(false);
  const [openEFT, setOpenEFT] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [transactions, setTransactions] = useState([]);

  // Listen to payments in Firestore
  useEffect(() => {
    if (!vendorId) return;

    const paymentsQuery = query(
      collection(db, 'vendors', vendorId, 'payments'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(paymentsQuery, async snapshot => {
const rows = await Promise.all(
  snapshot.docs.map(async paymentDoc => {
    const data = paymentDoc.data();
    const jobId = data.jobId;

    // fetch tenant address for propertyUnit
    let tenantAddress = 'Unknown address';
    try {
      const woSnap = await getDoc(doc(db, 'workOrders', jobId));
      if (woSnap.exists()) {
        const tenantUid = woSnap.data().tenantUid;
        if (tenantUid) {
          const userSnap = await getDoc(doc(db, 'users', tenantUid));
          if (userSnap.exists()) {
            tenantAddress = userSnap.data().address || tenantAddress;
          }
        }
      }
    } catch (err) {
      console.error('Error fetching tenant address:', err);
    }

    // pull amount & fee from Firestore and compute net
const amount    = data.amount    ?? 0;
const fee       = data.fee       ?? 0;
const netAmount = amount - fee;

return {
  id: paymentDoc.id,
  date: data.date,
  number: jobId,
  refNo: jobId,
  type: data.paymentMethod.toUpperCase(),
  propertyUnit: tenantAddress,
  account: 'Memphis Passive',
  memo: jobId,

  // ← these two lines make your new columns work:
  fee,
  netAmount,

  // keep gross for the existing column
  amount
};
  })
);
      setTransactions(rows);
    });

    return () => unsubscribe();
  }, [vendorId]);

const columns = [
  { field: 'date', headerName: 'Date', width: 120 },
  { field: 'number', headerName: 'Number', width: 150 },
  { field: 'refNo',  headerName: 'Ref. No.', width: 150 },
  { field: 'type',   headerName: 'Type',     width: 120 },
  { field: 'propertyUnit', headerName: 'Property – Unit', flex: 1 },
  { field: 'account', headerName: 'Account', width: 150 },
  { field: 'memo',    headerName: 'Memo',    width: 150 },

  // NEW: Service fee column
  {
    field: 'amount',
    headerName: 'Gross',
    width: 110,
    renderCell: params =>
      typeof params.value === 'number'
        ? `$${params.value.toFixed(2)}`
        : '—',
    cellClassName: 'text-right'
  },
  {
    field: 'fee',
    headerName: 'Fee',
    width: 110,
    renderCell: params =>
      typeof params.value === 'number'
        ? `$${params.value.toFixed(2)}`
        : '—',
    cellClassName: 'text-right'
  },
  {
    field: 'netAmount',
    headerName: 'Paid',
    width: 110,
    renderCell: params =>
      typeof params.value === 'number'
        ? `$${params.value.toFixed(2)}`
        : '—',
    cellClassName: 'text-right'
  }
];

  if (!vendor) return <Typography>Vendor not found</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      {/* Back + Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/pm/maintenance/vendors')}
        >
          Back
        </Button>
        <Typography variant="h4">
          {vendor.firstName} {vendor.lastName} • {vendor.category}
          <Link to="#" style={{ marginLeft: 12, color: '#007bff' }}>
            Edit
          </Link>
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" color="error">
          Inactivate vendor
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 3 }}
      >
        {tabLabels.map(label => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      {/* Summary Panel */}
   {/* Summary Panel */}
      {tabIndex === 0 && (
        <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
          <Typography variant="h6" gutterBottom>
            Vendor information <Link to="#" style={{ marginLeft: 8, color: '#007bff' }}>Edit</Link>
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
            {/* Left column */}
            <Box>
              <Typography variant="subtitle2">Expense account</Typography>
              <Typography>Repairs</Typography>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Comments</Typography>
              <Typography>—</Typography>
            </Box>
            {/* Middle column */}
            <Box>
              <Typography variant="subtitle2">Email</Typography>
              {vendor.email ? (
                <Link to={`mailto:${vendor.email}`} style={{ color: '#007bff' }}>
                  {vendor.email}
                </Link>
              ) : '—'}
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Phone</Typography>
              {vendor.phone ? (
                <>
                  {vendor.phone}
                  <br />
                  <Link to="#" style={{ color: '#007bff', fontSize: 12 }}>
                    Send opt-in text message
                  </Link>
                </>
              ) : '—'}
            </Box>
            {/* Right column */}
            <Box>
              <Typography variant="subtitle2">Address</Typography>
              <Typography>4135 Leroy Avenue<br />Memphis, TN 38108</Typography>
              <Link to="#" style={{ color: '#007bff' }}>Map it</Link>
            </Box>
          </Box>
          <Divider sx={{ my: 3 }} />
          {/* 1099 Section */}
          <Typography variant="h6" gutterBottom>
            1099-NEC tax filing <Link to="#" style={{ marginLeft: 8, color: '#007bff' }}>Edit</Link>
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
            <Box>
              <Typography variant="subtitle2">Taxpayer ID (TIN)</Typography>
              <Typography>
                *****1234 <Link to="#" style={{ fontSize: 12 }}>Show</Link>
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">Name</Typography>
              <Typography>{vendor.firstName} {vendor.lastName}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">Email</Typography>
              <Typography>Same as above</Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 3 }} />
          {/* Insurance */}
          <Typography variant="h6">
            Vendor insurance <Link to="#" style={{ marginLeft: 8, color: '#007bff' }}>Add</Link>
          </Typography>
          <Typography sx={{ color: '#777' }}>No vendor insurance information on file.</Typography>
          <Divider sx={{ my: 3 }} />
          {/* Custom fields */}
          <Typography variant="h6" gutterBottom>Custom fields</Typography>
          <Button variant="outlined" size="small">
            Add custom field
          </Button>
          <Typography sx={{ mt: 1, color: '#777' }}>
            You do not have any custom fields created to track additional information here.
          </Typography>
        </Paper>
      )}

      {/* Financials Panel */}
      {tabIndex === 1 && (
        <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="contained">Pay by check</Button>
            <Button variant="contained" onClick={() => setOpenEFT(true)}>
              Pay by EFT
            </Button>
   <Button
     variant="contained"
     color="secondary"
     onClick={() => setOpenInstant(true)}
   >
     Instant deposit
   </Button>
  </Box>       
      <EFTPaymentModal
        open={openEFT}
        onClose={() => setOpenEFT(false)}
        vendorId={vendorId}
        vendorName={`${vendor.firstName} ${vendor.lastName}`}
        paymentMethod="eft"      // flat $5 fee
      />
      {/* Instant deposit modal */}
      <EFTPaymentModal
        open={openInstant}
        onClose={() => setOpenInstant(false)}
        vendorId={vendorId}
        vendorName={`${vendor.firstName} ${vendor.lastName}`}
        paymentMethod="instant"  // 3% fee
      />
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={transactions}
              columns={columns}
              pageSize={5}
              rowsPerPageOptions={[5, 10]}
              disableSelectionOnClick
              onRowClick={params => navigate(`/pm/maintenance/work-orders/${params.row.number}`)}
            />
          </Box>
        </Paper>
      )}
      {/* Other tabs placeholder */}
      {tabIndex > 1 && (
        <Typography sx={{ color: '#777', fontStyle: 'italic' }}>
          {tabLabels[tabIndex]} content coming soon…
        </Typography>
      )}
    </Box>
  );
}
